<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="totp-modal__backdrop">
        <section class="totp-modal">
          <header class="totp-modal__header">
            <h3 class="totp-modal__title">{{ t('miniApp.totpTitle') }}</h3>
          </header>

          <div class="totp-modal__body">
            <div class="totp-modal__icon">
              <i class="zmdi zmdi-lock" />
            </div>

            <p class="totp-modal__hint">
              {{ t('miniApp.totpHint') }}
            </p>

            <!-- 6-digit code input -->
            <div class="totp-modal__code-input" @paste="handlePaste">
              <input
                v-for="(_, index) in 6"
                :key="index"
                ref="inputs"
                type="text"
                inputmode="numeric"
                maxlength="1"
                :value="digits[index]"
                @input="handleInput(index, $event)"
                @keydown.delete="handleDelete(index)"
                class="totp-modal__digit"
                :class="{
                  'totp-modal__digit--filled': digits[index],
                  'totp-modal__digit--error': hasError,
                }"
                :disabled="isLoading || isBlocked"
                autocomplete="one-time-code"
              />
            </div>

            <!-- Loading indicator -->
            <div v-if="isLoading" class="totp-modal__loading">
              <div class="totp-modal__spinner" />
              <span>{{ t('common.loading') }}</span>
            </div>

            <!-- Error message -->
            <Transition name="fade">
              <p v-if="error" class="totp-modal__error">
                <i class="zmdi zmdi-alert-circle" />
                {{ error }}
              </p>
            </Transition>

            <!-- Blocked state -->
            <div v-if="isBlocked" class="totp-modal__blocked">
              <i class="zmdi zmdi-time" />
              <span>{{ t('miniApp.totpBlocked') }}</span>
            </div>
          </div>
        </section>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TelegramAuthService } from './TelegramAuthService'

interface Props {
  visible: boolean
  spaAuthToken: string
}

interface Emits {
  (e: 'success', spaAuthToken: string): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()

const digits = ref<string[]>(['', '', '', '', '', ''])
const inputs = ref<HTMLInputElement[]>([])
const isLoading = ref(false)
const error = ref('')
const isBlocked = ref(false)
const hasError = ref(false)
let generation = 0
const retryToken = ref(props.spaAuthToken)

const code = computed(() => digits.value.join(''))
const isComplete = computed(() => code.value.length === 6 && digits.value.every(d => d !== ''))

function handleInput(index: number, event: Event) {
  const target = event.target as HTMLInputElement
  const value = target.value.replace(/\D/g, '')

  if (value) {
    digits.value[index] = value[0]
    hasError.value = false

    if (index < 5) {
      nextTick(() => inputs.value[index + 1]?.focus())
    }

    // Auto-submit when all 6 digits are filled
    nextTick(() => {
      if (isComplete.value) {
        submit()
      }
    })
  } else {
    digits.value[index] = ''
  }
}

function handleDelete(index: number) {
  if (digits.value[index]) {
    digits.value[index] = ''
    return
  }
  if (index > 0) {
    digits.value[index - 1] = ''
    inputs.value[index - 1]?.focus()
  }
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault()
  const pasted = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6)
  if (pasted) {
    hasError.value = false
    pasted.split('').forEach((char, i) => {
      digits.value[i] = char
    })
    const focusIndex = Math.min(pasted.length, 5)
    inputs.value[focusIndex]?.focus()

    nextTick(() => {
      if (isComplete.value) {
        submit()
      }
    })
  }
}

// Global keyboard handler — allows typing without focusing inputs
function handleGlobalKeydown(event: KeyboardEvent) {
  if (!props.visible || isLoading.value || isBlocked.value) return

  // Don't intercept if focused on other inputs
  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.totp-modal')
  if (isInputElement && !isModalElement) return

  // Digit keys
  if (/^[0-9]$/.test(event.key)) {
    event.preventDefault()
    hasError.value = false
    const emptyIndex = digits.value.findIndex(d => !d)
    if (emptyIndex !== -1) {
      digits.value[emptyIndex] = event.key
      const nextIndex = Math.min(emptyIndex + 1, 5)
      inputs.value[nextIndex]?.focus()

      nextTick(() => {
        if (isComplete.value) submit()
      })
    }
  }

  // Backspace — delete last filled digit
  if (event.key === 'Backspace') {
    const lastFilledIndex = digits.value.reduce((acc, d, i) => d !== '' ? i : acc, -1)
    if (lastFilledIndex !== -1) {
      event.preventDefault()
      digits.value[lastFilledIndex] = ''
      inputs.value[lastFilledIndex]?.focus()
    }
  }
}

// Global paste handler
function handleGlobalPaste(event: ClipboardEvent) {
  if (!props.visible || isLoading.value || isBlocked.value) return

  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.totp-modal')
  if (isInputElement && !isModalElement) return

  const paste = event.clipboardData?.getData('text')
  if (!paste) return

  const numbers = paste.match(/\d/g)
  if (numbers) {
    event.preventDefault()
    hasError.value = false
    numbers.slice(0, 6).forEach((num, i) => {
      digits.value[i] = num
    })
    inputs.value[Math.min(numbers.length, 5)]?.focus()

    if (numbers.length >= 6) {
      nextTick(() => submit())
    }
  }
}

async function submit() {
  if (!props.visible || !isComplete.value || isLoading.value || isBlocked.value) return
  const current = ++generation
  isLoading.value = true
  error.value = ''

  try {
    const result = await TelegramAuthService.verifyTotp(retryToken.value, code.value)
    if (current !== generation || !props.visible) return
    if (result.result === 'success' && result.spa_auth_token) {
      emit('success', result.spa_auth_token)
      return
    }
    if (result.result === 'error' && result.spa_auth_token) retryToken.value = result.spa_auth_token
    hasError.value = true
    if (result.result === 'error' && (result.message?.includes('blocked') || result.message?.includes('attempts'))) {
      isBlocked.value = true
      error.value = t('miniApp.totpBlocked')
    } else {
      error.value = t('miniApp.invalidCode')
      resetDigits()
    }
  } catch (e: unknown) {
    if (current !== generation || !props.visible) return
    hasError.value = true
    const failure = e as { response?: { status?: number; data?: { result?: string; spa_auth_token?: string } }; status?: number }
    const recovery = failure?.response?.data
    if (recovery?.result === 'error' && recovery.spa_auth_token) retryToken.value = recovery.spa_auth_token
    if ((failure?.response?.status ?? failure?.status) === 429) {
      isBlocked.value = true
      error.value = t('miniApp.totpBlocked')
    } else {
      error.value = t('miniApp.invalidCode')
      resetDigits()
    }
  } finally {
    if (current === generation) isLoading.value = false
  }
}

function resetDigits() {
  digits.value = ['', '', '', '', '', '']
  nextTick(() => inputs.value[0]?.focus())
}

// Add/remove global listeners
onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown)
  window.addEventListener('paste', handleGlobalPaste)
})

onUnmounted(() => {
  ++generation
  window.removeEventListener('keydown', handleGlobalKeydown)
  window.removeEventListener('paste', handleGlobalPaste)
})

// Reset state and focus when modal becomes visible
watch(() => [props.visible, props.spaAuthToken], ([visible]) => {
  ++generation
  isLoading.value = false
  if (visible) {
    retryToken.value = props.spaAuthToken
    resetDigits()
    error.value = ''
    isBlocked.value = false
    hasError.value = false
  }
})
</script>

<style scoped lang="scss">
.totp-modal__backdrop {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.totp-modal {
  width: min(420px, 90%);
  background: var(--ds-text-on-accent);
  border-radius: var(--ds-radius-modal);
  box-shadow: 0 24px 48px var(--ds-scrim);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.totp-modal__header {
  padding: 24px 24px 0;
  text-align: center;
}

.totp-modal__title {
  margin: 0;
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-bold);
  color: var(--hm-text-primary);
}

.totp-modal__body {
  padding: 16px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.totp-modal__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--hm-accent-soft, var(--ds-accent-soft-08));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  i {
    font-size: var(--p-text-28);
    color: var(--hm-accent);
  }
}

.totp-modal__hint {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
  text-align: center;
  margin: 0 0 24px;
  line-height: 1.5;
}

.totp-modal__code-input {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
}

.totp-modal__digit {
  width: 48px;
  height: 56px;
  border: 1px solid var(--hm-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--hm-accent);
    background: var(--ds-text-on-accent);
    box-shadow: 0 0 0 4px var(--hm-accent-soft, var(--ds-accent-soft-08));
  }

  &--filled {
    border-color: var(--hm-accent);
    background: var(--ds-text-on-accent);
  }

  &--error {
    border-color: var(--hm-danger);
    animation: shake 0.4s ease;

    &:focus {
      box-shadow: 0 0 0 4px var(--ds-danger-soft);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.totp-modal__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--hm-text-secondary);
  font-size: var(--ds-text-size-body);
  margin-bottom: 8px;
}

.totp-modal__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--hm-border);
  border-top-color: var(--hm-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.totp-modal__error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--p-text-13);
  color: var(--hm-danger);
  margin: 0;

  i {
    font-size: var(--ds-text-size-body-lg);
  }
}

.totp-modal__blocked {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--ds-text-size-body);
  color: var(--hm-warning);
  background: var(--ds-warning-soft);
  padding: 12px 16px;
  border-radius: var(--ds-radius-card);
  margin-top: 8px;

  i {
    font-size: var(--p-text-18);
  }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Mobile responsive
@media (max-width: 480px) {
  .totp-modal__code-input {
    gap: 6px;
  }

  .totp-modal__digit {
    width: 40px;
    height: 48px;
    font-size: var(--ds-text-size-h-sm);
    border-radius: var(--p-radius-lg);
  }
}
</style>
